import { Trophy, Clock, BarChart3, FileText, Calendar, MapPin, Target, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useUpcomingRacePreview } from "@/hooks/useUpcomingRacePreview";
import { format } from "date-fns";
import { cs } from "date-fns/locale";

const menuItems = [
  {
    title: "Výsledky",
    path: "/race-results",
    icon: Trophy,
    description: "Výsledky dostihů a statistiky"
  },
  {
    title: "Časy/distance",
    path: "/time-distance",
    icon: Clock,
    description: "Analýza časů a distancí"
  },
  {
    title: "Kurzy",
    path: "/odds-analysis",
    icon: BarChart3,
    description: "Sázkové kurzy a analýza"
  },
  {
    title: "Tipy",
    path: "/tip-analysis",
    icon: FileText,
    description: "Tipování dostihů a predikce"
  },
  {
    title: "Fantasy",
    path: "/fantasy",
    icon: Crown,
    description: "Fantasy liga"
  }
];

export const RacesMegaMenu = () => {
  const navigate = useNavigate();
  const { upcomingRace, topTips, isLoading } = useUpcomingRacePreview();

  return (
    <div className="grid grid-cols-2 gap-6 p-6 bg-[hsl(var(--header-dark))] w-[600px] rounded-lg border border-white/10 shadow-lg text-white">
      {/* Left Column - Menu Items */}
      <div className="space-y-2">
        {menuItems.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-white/10 transition-colors text-left group"
          >
            <div className="mt-0.5 p-2 rounded-md bg-white/10 group-hover:bg-white/20 transition-colors">
              <item.icon className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium mb-0.5 text-white">{item.title}</div>
              <p className="text-xs text-white/60 leading-relaxed">
                {item.description}
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* Right Column - Preview */}
      <div className="space-y-4">
        {/* Upcoming Race */}
        <Card
          className={`p-4 bg-white/5 border-white/10 transition-colors ${upcomingRace ? 'cursor-pointer hover:bg-white/10' : ''}`}
          onClick={() => upcomingRace && navigate(`/tipy-na-dostihy?eventId=${upcomingRace.event_id}`)}
        >
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-white" />
            <h3 className="text-sm font-semibold text-white">Nejbližší dostih</h3>
          </div>
          
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full bg-white/10" />
              <Skeleton className="h-4 w-3/4 bg-white/10" />
            </div>
          ) : upcomingRace ? (
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2 text-white/60">
                <MapPin className="h-3 w-3" />
                <span>{upcomingRace.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs bg-white/10 text-white border-white/20">
                  {format(new Date(upcomingRace.date), "d. MMMM", { locale: cs })}
                </Badge>
                <span className="text-white/60">{upcomingRace.time}</span>
              </div>
              <div className="flex gap-2 mt-2">
                <Badge variant="outline" className="text-xs border-white/20 text-white">
                  {upcomingRace.category}
                </Badge>
                <Badge variant="outline" className="text-xs border-white/20 text-white">
                  {upcomingRace.distance}
                </Badge>
              </div>
            </div>
          ) : (
            <p className="text-xs text-white/60">Nejsou k dispozici žádné nadcházející dostihy</p>
          )}
        </Card>

        {/* Top Tips */}
        <Card className="p-4 bg-white/5 border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <Target className="h-4 w-4 text-white" />
            <h3 className="text-sm font-semibold text-white">Top 3 tipy</h3>
          </div>
          
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full bg-white/10" />
              <Skeleton className="h-4 w-5/6 bg-white/10" />
              <Skeleton className="h-4 w-4/6 bg-white/10" />
            </div>
          ) : topTips && topTips.length > 0 ? (
            <div className="space-y-2">
              {topTips.map((tip, index) => (
                <div key={index} className="flex items-start justify-between text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate text-white">{tip.horse_name}</div>
                    <div className="text-white/60 text-[10px] truncate">
                      {tip.jockey_name}
                    </div>
                  </div>
                  <Badge variant="secondary" className="ml-2 text-[10px] shrink-0 bg-white/10 text-white border-white/20">
                    {tip.tip_count} {tip.tip_count === 1 ? 'tip' : 'tipů'}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-white/60">Zatím žádné tipy</p>
          )}
        </Card>
      </div>
    </div>
  );
};
